import {
  circulatingField,
  tokenSupplyFields,
  useApi,
  useCirculatingSupply
} from '@/api';
import {
  ActivityCard,
  EntityName,
  KpiCard,
  Link,
  Page,
  PageSkeleton
} from '@/components';
import {
  CryptoIcon,
  EtherScanPictogram,
  Flex,
  Heading,
  LinkWrapper
} from '@blockanalitica/ui';
import { useParams } from 'react-router-dom';
import HistoricTabs from './HistoricTabs';
import HoldersDonutChart from './HoldersDonutChart';
import TopHolders from './TopHolders';

export default function TokensPage() {
  const { address } = useParams();

  const [data, error] = useApi({
    url: `/tokens/${address}/`,
    queryParams: {
      days_ago: 1
    }
  });

  const [supply, supplyError] = useCirculatingSupply(1);

  if (error) {
    throw error;
  }

  const supplyField = data ? circulatingField(data.symbol) : undefined;
  if (supplyField && supplyError) {
    throw supplyError;
  }

  if (data && (!supplyField || supply)) {
    const kpiData = supplyField
      ? {
          ...data,
          ...tokenSupplyFields(supply, supplyField, data.price, data.price_prev)
        }
      : data;
    let variant: string;

    switch (data?.symbol) {
      case 'USDS':
        variant = 'usds';
        break;
      case 'sUSDS':
        variant = 'susds';
        break;
      case 'SKY':
        variant = 'sky';
        break;
      case 'DAI':
        variant = 'dai';
        break;
      case 'MKR':
        variant = 'mkr';
        break;
      case 'sDAI':
        variant = 'sdai';
        break;
      default:
        variant = 'default';
        break;
    }

    // const getDualIcon = (symbol) => {
    //   switch (symbol) {
    //     case 'MKR':
    //     case 'SKY':
    //       return (
    //         <KpiEntityName
    //           symbol1={
    //             <CryptoIcon
    //               name={tokensMapping('MKR').name}
    //               address={tokensMapping('MKR').address}
    //               size="3"
    //             />
    //           }
    //           symbol2={
    //             <CryptoIcon
    //               name={tokensMapping('SKY').name}
    //               address={tokensMapping('SKY').address}
    //               size="3"
    //             />
    //           }></KpiEntityName>
    //       );
    //     case 'USDS':
    //     case 'DAI':
    //       return (
    //         <KpiEntityName
    //           symbol1={
    //             <CryptoIcon
    //               name={tokensMapping('DAI').name}
    //               address={tokensMapping('DAI').address}
    //               size="3"
    //             />
    //           }
    //           symbol2={
    //             <CryptoIcon
    //               name={tokensMapping('USDS').name}
    //               address={tokensMapping('USDS').address}
    //               size="3"
    //             />
    //           }></KpiEntityName>
    //       );
    //     default:
    //       return null;
    //   }
    // };

    // const dualIcon = getDualIcon(data.symbol);

    return (
      <Page
        title={
          <EntityName
            symbol1={
              <CryptoIcon name={data.symbol} address={address} size="3" />
            }>
            <Heading size="1">
              <Flex gap="xs">
                {data.symbol}
                <a
                  href={`https://etherscan.io/address/${address}`}
                  target="_blank"
                  rel="noreferrer">
                  <EtherScanPictogram size="0" />
                </a>
              </Flex>
            </Heading>
          </EntityName>
        }>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Circulating Supply"
            data={kpiData}
            variant={variant}
            field="total_corrected"
            options={{
              currency: {
                name: data.symbol,
                address: address
              },
              notation: 'standard'
            }}
          />
        </Flex>

        <HistoricTabs address={address} />
        <Flex
          flexBasis="100%"
          gap={['m', 's']}
          flexDirection={['column', 'row']}>
          <KpiCard
            title="Market Cap"
            data={kpiData}
            field="market_cap_corrected"
            options={{
              currency: 'USD',
              notation: 'standard'
            }}
          />
          <KpiCard
            title="Price"
            data={data}
            field="price"
            options={{
              currency: 'USD',
              notation: 'standard',
              valueOptions: {
                currencyValueOptions: {
                  formatCurrencyOptions: {
                    maximumFractionDigits: 4
                  }
                }
              }
            }}
          />
        </Flex>
        <ActivityCard
          url={`/tokens/${address}/activity/`}
          linkTo="activity"
          outlierMultiplier={variant === 'dai' ? 5 : undefined}
        />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="holders"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Holders"
                data={data}
                field="holders"
                options={{ linkArrow: true }}
              />
            </LinkWrapper>
            <TopHolders address={address} flexBasis="100%" />
          </Flex>
          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="3">Holders Distribution</Heading>
            <HoldersDonutChart address={address} />
          </Flex>
        </Flex>
      </Page>
    );
  }
  return <PageSkeleton />;
}
